import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { getApiBase } from './Config';
import './dashboard.css';

function numericMetric(metrics, key) {
  if (!metrics || typeof metrics !== 'object') return null;
  const v = metrics[key];
  if (v == null) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

const ASSS_MAX = 24;
const RECORDING_PAGE_KEYS = new Set(['page_2', 'page_3']);

/** Absolute EEG: round(100 × delta_relative_clean), clamped 0–100. */
function eegScore(deltaRelativeClean) {
  if (deltaRelativeClean == null || !Number.isFinite(deltaRelativeClean)) return null;
  if (deltaRelativeClean < 0) return 0;
  return Math.max(0, Math.min(100, Math.round(100 * deltaRelativeClean)));
}

/** ASSS wellness index: higher = better. totalScore is raw 0–24. */
function asssIndex(totalScore) {
  if (totalScore == null || !Number.isFinite(Number(totalScore))) return null;
  const t = Math.max(0, Math.min(ASSS_MAX, Number(totalScore)));
  return Math.max(0, Math.min(100, Math.round((100 * (ASSS_MAX - t)) / ASSS_MAX)));
}

/** Progress = 0.5×EEG + 0.5×ASSS index; EEG-only if ASSS missing. */
function progressScore(eeg, asssIdx) {
  if (eeg == null) return null;
  if (asssIdx == null) return eeg;
  return Math.max(0, Math.min(100, Math.round((eeg + asssIdx) / 2)));
}

function submissionTotal(sub) {
  if (!sub || typeof sub !== 'object') return null;
  if (sub.total_score != null && Number.isFinite(Number(sub.total_score))) {
    return Number(sub.total_score);
  }
  const payload = sub.payload;
  if (payload && typeof payload === 'object' && payload.total_score != null) {
    const n = Number(payload.total_score);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function recordTimeMs(record) {
  const iso = record?.created_at || record?.ready_at;
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Pair each ready song with at most one page_2/page_3 ASSS submission.
 * 1) Exact match on recording_context (BLE session id)
 * 2) Fallback: answered_at ≤ recording time, nearest before; 1:1 oldest song first
 * Returns Map-like object: songId -> submission.
 */
function pairAsssToRecords(readyRecords, submissions) {
  const candidates = (submissions || []).filter((s) => {
    const pk = s?.page_key;
    if (!pk || !RECORDING_PAGE_KEYS.has(String(pk))) return false;
    return submissionTotal(s) != null && s.answered_at;
  });

  const used = new Set();
  const out = {};

  const byContext = {};
  for (const sub of candidates) {
    const ctx = sub.recording_context != null ? String(sub.recording_context).trim() : '';
    if (!ctx) continue;
    const prev = byContext[ctx];
    if (!prev) {
      byContext[ctx] = sub;
      continue;
    }
    const pt = new Date(prev.answered_at).getTime();
    const st = new Date(sub.answered_at).getTime();
    if (!Number.isNaN(st) && (Number.isNaN(pt) || st < pt)) {
      byContext[ctx] = sub;
    }
  }

  for (const song of readyRecords || []) {
    if (song?.id == null) continue;
    const ctx = song.recording_context != null ? String(song.recording_context).trim() : '';
    if (!ctx) continue;
    const sub = byContext[ctx];
    if (!sub) continue;
    if (sub.id != null && used.has(sub.id)) continue;
    if (sub.id != null) used.add(sub.id);
    out[song.id] = sub;
  }

  const songsOldestFirst = (readyRecords || []).slice().sort((a, b) => {
    const ta = recordTimeMs(a) ?? 0;
    const tb = recordTimeMs(b) ?? 0;
    return ta - tb;
  });

  for (const song of songsOldestFirst) {
    if (song?.id == null || out[song.id]) continue;
    const songT = recordTimeMs(song);
    if (songT == null) continue;

    let best = null;
    let bestT = null;
    for (const sub of candidates) {
      if (sub.id != null && used.has(sub.id)) continue;
      const at = new Date(sub.answered_at).getTime();
      if (Number.isNaN(at) || at > songT) continue;
      if (best == null || at > bestT) {
        best = sub;
        bestT = at;
      }
    }
    if (best) {
      if (best.id != null) used.add(best.id);
      out[song.id] = best;
    }
  }
  return out;
}

function progressScoreForSong(song, asssBySongId) {
  const eeg = eegScore(numericMetric(song?.report_metrics, 'delta_relative_clean'));
  if (eeg == null) return null;
  const sub = asssBySongId && song?.id != null ? asssBySongId[song.id] : null;
  const idx = sub ? asssIndex(submissionTotal(sub)) : null;
  return progressScore(eeg, idx);
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function ymdLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatPointValue(v) {
  if (v == null || !Number.isFinite(v)) return '';
  const abs = Math.abs(v);
  if (abs >= 100) return v.toFixed(1);
  return v.toFixed(2);
}

function LineChart({ points, height = 240, yMin = 0, yMax = 200, yLabel = '', color = '#1d4ed8' }) {
  const width = 720;
  const pad = { top: 28, right: 28, bottom: 40, left: 48 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const usable = (points || []).filter((p) => p.value != null && Number.isFinite(p.value));

  if (usable.length === 0) {
    return (
      <div className="flex h-56 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-500">
        No chart data yet
      </div>
    );
  }

  const span = Math.max(yMax - yMin, 1);
  const xAt = (i) => pad.left + (usable.length === 1 ? innerW / 2 : (i / (usable.length - 1)) * innerW);
  const yAt = (v) => pad.top + innerH - ((v - yMin) / span) * innerH;
  const path = usable
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(p.value).toFixed(1)}`)
    .join(' ');

  const gridYs = [0, 0.25, 0.5, 0.75, 1].map((t) => yMin + span * t);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full" role="img">
      {gridYs.map((gv) => (
        <g key={gv}>
          <line
            x1={pad.left}
            x2={width - pad.right}
            y1={yAt(gv)}
            y2={yAt(gv)}
            stroke="#e5e7eb"
            strokeWidth="1"
          />
          <text x={pad.left - 8} y={yAt(gv) + 4} textAnchor="end" fontSize="11" fill="#6b7280">
            {Number.isInteger(gv) ? gv : gv.toFixed(2)}
          </text>
        </g>
      ))}
      <path d={path} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {usable.map((p, i) => {
        const cx = xAt(i);
        const cy = yAt(p.value);
        const labelAbove = cy - pad.top >= 16;
        const valueText = formatPointValue(p.value);
        return (
          <g key={`${p.label}-${i}`}>
            <circle cx={cx} cy={cy} r="4.5" fill={color} />
            <text
              x={cx}
              y={labelAbove ? cy - 10 : cy + 16}
              textAnchor="middle"
              fontSize="11"
              fontWeight="600"
              fill="#111827"
            >
              {valueText}
            </text>
            <text x={cx} y={height - 14} textAnchor="middle" fontSize="11" fill="#6b7280">
              {p.label}
            </text>
            <title>{`${p.label}: ${valueText}${yLabel ? ` ${yLabel}` : ''}`}</title>
          </g>
        );
      })}
    </svg>
  );
}

function ListeningCalendar({ dates, latestReadyAt }) {
  const listened = useMemo(() => new Set((dates || []).map((d) => String(d).slice(0, 10))), [dates]);
  const [cursor, setCursor] = useState(() => {
    const base = latestReadyAt ? new Date(latestReadyAt) : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const boundary = latestReadyAt ? new Date(latestReadyAt) : null;
  const boundaryDay = boundary
    ? new Date(boundary.getFullYear(), boundary.getMonth(), boundary.getDate())
    : null;
  const today = new Date();
  const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const inWindow = (day) => {
    if (!boundaryDay) return false;
    const start = new Date(boundaryDay);
    start.setDate(start.getDate() + 1);
    return day >= start && day <= todayDay;
  };

  let windowTotal = 0;
  let windowListened = 0;
  if (boundaryDay) {
    const start = new Date(boundaryDay);
    start.setDate(start.getDate() + 1);
    for (let t = new Date(start); t <= todayDay; t.setDate(t.getDate() + 1)) {
      windowTotal += 1;
      if (listened.has(ymdLocal(t))) windowListened += 1;
    }
  }

  const weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <div style={{ maxWidth: 420 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
          gap: 12,
        }}
      >
        <button
          type="button"
          onClick={() => setCursor(new Date(year, month - 1, 1))}
          style={{
            width: 36,
            height: 36,
            borderRadius: '999px',
            border: '1px solid #d1d5db',
            background: '#fff',
            cursor: 'pointer',
            fontSize: 16,
          }}
          aria-label="Previous month"
        >
          ‹
        </button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#111827' }}>
            {cursor.toLocaleString(undefined, { month: 'long', year: 'numeric' })}
          </div>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
            {windowListened} of {windowTotal || '—'} nights listened in full
          </div>
        </div>
        <button
          type="button"
          onClick={() => setCursor(new Date(year, month + 1, 1))}
          style={{
            width: 36,
            height: 36,
            borderRadius: '999px',
            border: '1px solid #d1d5db',
            background: '#fff',
            cursor: 'pointer',
            fontSize: 16,
          }}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          fontSize: 12,
          color: '#6b7280',
          marginBottom: 12,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: '#10b981' }} />
          Listened
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: '#fb7185' }} />
          Missed
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: 3,
              background: '#fff',
              border: '1px solid #d1d5db',
            }}
          />
          Outside window
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gap: 6,
          marginBottom: 6,
        }}
      >
        {weekdays.map((d, i) => (
          <div
            key={`${d}-${i}`}
            style={{
              textAlign: 'center',
              fontSize: 12,
              fontWeight: 600,
              color: '#9ca3af',
              padding: '4px 0',
            }}
          >
            {d}
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gap: 6,
        }}
      >
        {cells.map((day, idx) => {
          if (!day) {
            return <div key={`e-${idx}`} style={{ aspectRatio: '1 / 1' }} />;
          }
          const key = ymdLocal(day);
          const listenedDay = listened.has(key);
          const windowDay = inWindow(day);
          const isToday = key === ymdLocal(todayDay);

          let background = '#ffffff';
          let color = '#374151';
          let border = '1px solid #e5e7eb';
          let fontWeight = 500;
          if (listenedDay) {
            background = '#10b981';
            color = '#ffffff';
            border = '1px solid #059669';
            fontWeight = 700;
          } else if (windowDay) {
            background = '#ffe4e6';
            color = '#9f1239';
            border = '1px solid #fda4af';
          }
          if (isToday && !listenedDay) {
            border = '2px solid #1d4ed8';
          }

          return (
            <div
              key={key}
              title={key}
              style={{
                aspectRatio: '1 / 1',
                borderRadius: 10,
                border,
                background,
                color,
                fontWeight,
                fontSize: 14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 40,
              }}
            >
              {day.getDate()}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PatientReportPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [songs, setSongs] = useState([]);
  const [listening, setListening] = useState({ dates: [], latest_recording_ready_at: null });
  const [qnrSubmissions, setQnrSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const accessToken = Cookies.get('access_token');
    if (!accessToken) {
      navigate('/login');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const base = getApiBase();
      const [userRes, songsRes, listenRes, qnrRes] = await Promise.all([
        fetch(`${base}/admin/users/${userId}`, { headers }),
        fetch(`${base}/admin/users/${userId}/songs`, { headers }),
        fetch(`${base}/admin/users/${userId}/listening-dates`, { headers }),
        fetch(`${base}/admin/questionnaire/users/${userId}/submissions`, { headers }),
      ]);
      if (userRes.status === 401 || songsRes.status === 401 || listenRes.status === 401 || qnrRes.status === 401) {
        navigate('/login');
        return;
      }
      if (!userRes.ok) throw new Error('Failed to load patient');
      if (!songsRes.ok) throw new Error('Failed to load recordings');
      if (!listenRes.ok) throw new Error('Failed to load listening calendar');
      setPatient(await userRes.json());
      setSongs(await songsRes.json());
      setListening(await listenRes.json());
      if (qnrRes.ok) {
        setQnrSubmissions(await qnrRes.json());
      } else {
        setQnrSubmissions([]);
      }
    } catch (e) {
      setError(e.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [navigate, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const readySongs = useMemo(() => {
    return (songs || [])
      .filter((s) => (s.status || '').toLowerCase() === 'ready' && s.report_metrics)
      .slice()
      .sort((a, b) => {
        const ta = new Date(a.ready_at || a.created_at || 0).getTime();
        const tb = new Date(b.ready_at || b.created_at || 0).getTime();
        return tb - ta;
      });
  }, [songs]);

  const asssBySongId = useMemo(
    () => pairAsssToRecords(readySongs, qnrSubmissions),
    [readySongs, qnrSubmissions],
  );

  const scoreSeries = useMemo(() => {
    return readySongs
      .slice(0, 8)
      .reverse()
      .map((s, i) => ({
        label: formatDate(s.ready_at || s.created_at) || `#${i + 1}`,
        value: progressScoreForSong(s, asssBySongId),
        song: s,
      }));
  }, [asssBySongId, readySongs]);

  const latestScore = scoreSeries.length ? scoreSeries[scoreSeries.length - 1].value : null;
  const previousScore =
    scoreSeries.length >= 2 ? scoreSeries[scoreSeries.length - 2].value : null;
  const latestSong = readySongs.length ? readySongs[0] : null;
  const latestEegOnly =
    latestSong != null &&
    latestScore != null &&
    !(latestSong.id != null && asssBySongId[latestSong.id]);

  const statusLine = (() => {
    if (latestScore == null) return null;
    if (previousScore == null || readySongs.length === 1) return 'Sleep Score ready';
    if (latestScore > previousScore) return 'Improved since previous recording';
    if (latestScore < previousScore) return 'Lower than previous recording';
    return 'Similar to previous recording';
  })();

  const deltaBalanceSeries = useMemo(() => {
    return readySongs
      .slice(0, 8)
      .reverse()
      .map((s, i) => ({
        label: formatDate(s.ready_at || s.created_at) || `#${i + 1}`,
        value: numericMetric(s.report_metrics, 'delta_relative_clean'),
      }));
  }, [readySongs]);

  const metricSeries = (key, scale = 1) =>
    readySongs
      .slice(0, 8)
      .reverse()
      .map((s, i) => {
        const v = numericMetric(s.report_metrics, key);
        return {
          label: formatDate(s.ready_at || s.created_at) || `#${i + 1}`,
          value: v == null ? null : v * scale,
        };
      });

  const peakPowerSeries = metricSeries('delta_peak_power');
  const peakRatioSeries = metricSeries('delta_peak_to_background_ratio');
  const peakHzSeries = metricSeries('delta_peak_hz');

  const autoYMax = (pts, fallback) => {
    const vals = pts.map((p) => p.value).filter((v) => v != null && Number.isFinite(v));
    if (!vals.length) return fallback;
    return Math.max(fallback, Math.ceil(Math.max(...vals) * 1.15));
  };

  const qnrScoreSeries = useMemo(() => {
    return (qnrSubmissions || [])
      .map((s) => {
        const score = numericMetric({ total_score: s.total_score }, 'total_score');
        if (score == null) return null;
        const answered = s.answered_at || '';
        const t = new Date(answered).getTime();
        if (!answered || Number.isNaN(t)) return null;
        return {
          label: formatDate(answered),
          value: score,
          t,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.t - b.t)
      .map(({ label, value }) => ({ label, value }));
  }, [qnrSubmissions]);

  const qnrYMax = useMemo(() => autoYMax(qnrScoreSeries, 10), [qnrScoreSeries]);

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-4">
          <div>
            <Link to="/dashboard" className="text-sm font-medium text-blue-700 hover:text-blue-900">
              ← Back to patients
            </Link>
            <h1 className="mt-1 text-2xl font-semibold text-gray-900">
              Patient report{patient?.name ? `: ${patient.name}` : ''}
            </h1>
            <p className="text-sm text-gray-500">Progress graphs and listening calendar</p>
          </div>
          <button
            type="button"
            onClick={load}
            className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            Refresh
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-6 px-6 py-8">
        {loading && (
          <div className="flex h-40 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
          </div>
        )}

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">{error}</div>
        )}

        {!loading && !error && patient && (
          <>
            <section className="grid grid-cols-1 gap-4 rounded-xl bg-white p-6 shadow-sm lg:grid-cols-4">
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">Patient</div>
                <div className="mt-1 font-semibold text-gray-900">{patient.name}</div>
                <div className="text-sm text-gray-600">ID {patient.id}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">Contact</div>
                <div className="mt-1 text-sm text-gray-900">{patient.email}</div>
                <div className="text-sm text-gray-600">{patient.number}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">Profile</div>
                <div className="mt-1 text-sm text-gray-900">{patient.country || '—'}</div>
                <div className="text-sm text-gray-600">{patient.gender || '—'} · DOB {formatDate(patient.dob)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">Access</div>
                <div className="mt-1 text-sm font-medium text-gray-900">
                  {patient.subscription_status || 'none'}
                  {patient.subscription_plan ? ` · ${patient.subscription_plan}` : ''}
                </div>
                <div className="text-sm text-gray-600">
                  Store: {patient.subscription_store || '—'}
                </div>
              </div>
            </section>

            <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="rounded-xl bg-white p-6 shadow-sm lg:col-span-1">
                <div className="text-xs uppercase tracking-wide text-gray-500">Sleep Score</div>
                <div className="mt-2 text-5xl font-semibold text-gray-900">
                  {latestScore != null ? latestScore : '—'}
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  50% brain signal (100×δ) + 50% ASSS index when paired; EEG-only otherwise.
                  Latest of {readySongs.length} ready recording
                  {readySongs.length === 1 ? '' : 's'}.
                </p>
                {statusLine && (
                  <p className="mt-3 text-sm font-medium text-blue-800">{statusLine}</p>
                )}
                {latestEegOnly && (
                  <p className="mt-1 text-xs text-amber-700">
                    No linked ASSS for latest — EEG only
                  </p>
                )}
              </div>

              <div className="rounded-xl bg-white p-6 shadow-sm lg:col-span-2">
                <div className="mb-2 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Sleep Score over time</h2>
                  <span className="text-xs text-gray-500">Last {Math.min(8, readySongs.length)} sessions · 0–100</span>
                </div>
                <LineChart points={scoreSeries} yMin={0} yMax={100} color="#1d4ed8" />
              </div>
            </section>

            <section className="rounded-xl bg-white p-6 shadow-sm">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Questionnaire scores</h2>
                  <p className="text-sm text-gray-500">
                    Raw ASSS totals (0–24; lower is better sleep). page_2/page_3 pair into Sleep Score.
                  </p>
                </div>
                <span className="text-xs text-gray-500">{qnrScoreSeries.length} submission{qnrScoreSeries.length === 1 ? '' : 's'}</span>
              </div>
              <LineChart points={qnrScoreSeries} yMin={0} yMax={qnrYMax} color="#0f766e" />
            </section>

            <section className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <div className="rounded-xl bg-white p-6 shadow-sm">
                <h2 className="mb-2 text-lg font-semibold text-gray-900">Delta balance</h2>
                <p className="mb-3 text-sm text-gray-500">delta_relative_clean (0–1)</p>
                <LineChart
                  points={deltaBalanceSeries}
                  yMin={0}
                  yMax={1}
                  color="#0f766e"
                />
              </div>
              <div className="rounded-xl bg-white p-6 shadow-sm">
                <h2 className="mb-2 text-lg font-semibold text-gray-900">Delta peak power</h2>
                <p className="mb-3 text-sm text-gray-500">Developer metric</p>
                <LineChart
                  points={peakPowerSeries}
                  yMin={0}
                  yMax={autoYMax(peakPowerSeries, 1)}
                  color="#7c3aed"
                />
              </div>
              <div className="rounded-xl bg-white p-6 shadow-sm">
                <h2 className="mb-2 text-lg font-semibold text-gray-900">Peak-to-background ratio</h2>
                <LineChart
                  points={peakRatioSeries}
                  yMin={0}
                  yMax={autoYMax(peakRatioSeries, 1)}
                  color="#c2410c"
                />
              </div>
              <div className="rounded-xl bg-white p-6 shadow-sm">
                <h2 className="mb-2 text-lg font-semibold text-gray-900">Delta peak frequency (Hz)</h2>
                <LineChart
                  points={peakHzSeries}
                  yMin={0}
                  yMax={autoYMax(peakHzSeries, 4)}
                  color="#be185d"
                />
              </div>
            </section>

            <section className="rounded-xl bg-white p-6 shadow-sm">
              <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Listening calendar</h2>
                  <p className="text-sm text-gray-500">
                    Full listens after latest recording
                    {listening.latest_recording_ready_at
                      ? ` (ready ${formatDateTime(listening.latest_recording_ready_at)})`
                      : ''}
                    . Counter: {listening.days_full_listened_after_latest ?? 0}
                  </p>
                </div>
              </div>
              <ListeningCalendar
                dates={listening.dates || []}
                latestReadyAt={listening.latest_recording_ready_at}
              />
            </section>

            <section className="rounded-xl bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold text-gray-900">Recording history</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">ID</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Title</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Ready</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Expires</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Sleep Score</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">Listened / window</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {songs.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                          No recordings for this patient.
                        </td>
                      </tr>
                    )}
                    {songs.map((s) => {
                      const score = progressScoreForSong(s, asssBySongId);
                      return (
                        <tr key={s.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-500">{s.id}</td>
                          <td className="px-4 py-3 font-medium text-gray-900">{s.title || '—'}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                                s.status === 'ready'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : s.status === 'pending'
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {s.status || '—'}
                              {s.is_expired ? ' · expired' : ''}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{formatDateTime(s.ready_at)}</td>
                          <td className="px-4 py-3 text-gray-600">{formatDateTime(s.expires_at)}</td>
                          <td className="px-4 py-3 font-semibold text-gray-900">{score ?? '—'}</td>
                          <td className="px-4 py-3 text-gray-600">
                            {s.days_listened_full_since_previous ?? '—'}
                            {' / '}
                            {s.total_days_since_previous ?? '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

export default PatientReportPage;
